import { useCallback, useEffect } from "react";
import { useListedAlgo } from "../lazertrader/contexts/listAlgosContext";
import { usePrefillContext } from "../../Services/PrefillContextProvider";
import { behaviorFromAlgoSpec } from "./algoSpec";
import { inFloatingLayer } from "../../floatingLayer";

/**
 * Whether the config form should be on screen, and how to dismiss it.
 *
 * There is no `isOpen` flag anywhere: the popup is a pure function of the two
 * things that already mean "the user is composing an algo" — a prefill from the
 * order book (adding) or a single editable algo selected in a datagrid
 * (editing). Anything that clears those — submitting, cancelling, the
 * click-outside handler in `ListedAlgoProvider` — closes the popup for free.
 *
 * Selecting an algo this tab cannot edit deliberately does not open it.
 */
export const useConfigPopup = () => {
  const { selectedAlgos, setSelectedAlgos } = useListedAlgo();
  const { prefill, reset } = usePrefillContext();

  const editing =
    selectedAlgos.length === 1 &&
    behaviorFromAlgoSpec(selectedAlgos[0].algo_spec.name) !== undefined;

  const open = prefill !== undefined || editing;

  const close = useCallback(() => {
    setSelectedAlgos([]);
    reset();
  }, [setSelectedAlgos, reset]);

  return { open, editing, close };
};

/**
 * Dismisses the "adding" case when you click away from the popup.
 *
 * Editing already closed this way for free: `ListedAlgoProvider` clears the
 * selection on any mouseup outside the refs registered with it, and the popup is
 * one of those. Nothing did the equivalent for a prefill.
 *
 * This clears only the prefill. Whether a click should drop the *selection* is
 * that provider's business — it has exclusions (the algo grid, the stop button)
 * this hook knows nothing about, and clearing selection here would fight it.
 *
 * Re-prefilling still works: the order book sets its prefill from a click, which
 * lands after the mouseup that clears the previous one.
 */
export const useClearPrefillOnClickOutside = (
  panelRef: React.RefObject<HTMLElement | null>
) => {
  const { prefill, reset } = usePrefillContext();
  const { excludedRefs } = useListedAlgo();
  useEffect(() => {
    if (prefill === undefined) {
      return;
    }
    const onMouseUp = (event: MouseEvent) => {
      // Ctrl-click is the grid's multi-select gesture; the selection handler
      // ignores it, so this does too.
      if (event.ctrlKey) {
        return;
      }
      if (panelRef.current?.contains(event.target as Node)) {
        return;
      }
      if (inFloatingLayer(event.target)) {
        return;
      }
      if (
        excludedRefs.some((ref) => ref.current?.contains(event.target as Node))
      ) {
        return;
      }
      reset();
    };
    document.addEventListener("mouseup", onMouseUp);
    return () => document.removeEventListener("mouseup", onMouseUp);
  }, [prefill, reset, panelRef, excludedRefs]);
};

/** Escape closes the popup, matching what people expect of a dialog. */
export const useCloseOnEscape = (enabled: boolean, close: () => void) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      // An open dropdown gets the first Escape — it closes itself, and the form
      // stays put. A second Escape then closes the form.
      if (inFloatingLayer(event.target)) {
        return;
      }
      close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [enabled, close]);
};
